// Пресеты периода для аналитики: день / 2 дня / неделя / месяц / квартал / год
// в вариантах «текущий» и «предыдущий», плюс произвольный период.
//
// Всё считается в часовом поясе браузера владельца. Бэкенд нарезает часы и дни
// по TZ клуба (settings.bookingUtcOffsetMinutes) — при удалённом просмотре из
// другого пояса границы суток могут разойтись на час-другой.

export type PeriodUnit = 'day' | 'day2' | 'week' | 'month' | 'quarter' | 'year' | 'custom'
export type PeriodShift = 'current' | 'previous'

export interface PeriodRange {
  from: number
  to: number
  /** Человекочитаемая подпись периода для шапки отчёта. */
  label: string
  /** Период не закончился — данные «на текущий момент». */
  partial: boolean
}

export const PERIOD_UNITS: ReadonlyArray<{ unit: PeriodUnit; label: string }> = [
  { unit: 'day', label: 'День' },
  { unit: 'day2', label: '2 дня' },
  { unit: 'week', label: 'Неделя' },
  { unit: 'month', label: 'Месяц' },
  { unit: 'quarter', label: 'Квартал' },
  { unit: 'year', label: 'Год' },
  { unit: 'custom', label: 'Свой период' }
]

/** Подписи переключателя «текущий/предыдущий» — они разные для дня и для календарных единиц. */
export const SHIFT_LABELS: Record<PeriodUnit, { current: string; previous: string }> = {
  day: { current: 'Сегодня', previous: 'Вчера' },
  day2: { current: 'Последние 2 дня', previous: 'Предыдущие 2 дня' },
  week: { current: 'Текущая', previous: 'Предыдущая' },
  month: { current: 'Текущий', previous: 'Предыдущий' },
  quarter: { current: 'Текущий', previous: 'Предыдущий' },
  year: { current: 'Текущий', previous: 'Предыдущий' },
  custom: { current: '—', previous: '—' }
}

const DAY_MS = 24 * 60 * 60 * 1000

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime()
}

/** Понедельник недели, в которую попал `d` (неделя ISO: Пн–Вс). */
function startOfWeek(d: Date): number {
  const wd = (d.getDay() + 6) % 7 // 0 = понедельник
  return startOfDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() - wd))
}

function fmt(ms: number): string {
  return new Date(ms).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
}

function fmtFull(ms: number): string {
  return new Date(ms).toLocaleDateString('ru-RU')
}

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь'
]

/**
 * Границы периода по пресету. Конец «текущего» периода подрезается текущим
 * моментом: иначе график текущего месяца тянул бы хвост из пустых будущих дней.
 */
export function resolveRange(
  unit: PeriodUnit,
  shift: PeriodShift,
  customFrom: string,
  customTo: string,
  now = Date.now()
): PeriodRange {
  const nowDate = new Date(now)
  const today = startOfDay(nowDate)
  const prev = shift === 'previous'

  const clamp = (from: number, end: number, label: string): PeriodRange => {
    const to = Math.min(end, now)
    return { from, to, label, partial: end > now }
  }

  switch (unit) {
    case 'day': {
      const from = prev ? today - DAY_MS : today
      return clamp(from, from + DAY_MS - 1, `${prev ? 'Вчера' : 'Сегодня'}, ${fmtFull(from)}`)
    }
    case 'day2': {
      const end = prev ? today - 2 * DAY_MS : today
      const from = end - DAY_MS
      return clamp(from, end + DAY_MS - 1, `${fmt(from)} — ${fmt(end)}`)
    }
    case 'week': {
      const start = startOfWeek(nowDate) - (prev ? 7 * DAY_MS : 0)
      const end = start + 7 * DAY_MS - 1
      return clamp(start, end, `${prev ? 'Пред. неделя' : 'Неделя'}: ${fmt(start)} — ${fmt(end)}`)
    }
    case 'month': {
      const m = nowDate.getMonth() - (prev ? 1 : 0)
      const start = new Date(nowDate.getFullYear(), m, 1).getTime()
      const end = new Date(nowDate.getFullYear(), m + 1, 1).getTime() - 1
      const sd = new Date(start)
      return clamp(start, end, `${MONTHS[sd.getMonth()]} ${sd.getFullYear()}`)
    }
    case 'quarter': {
      const q = Math.floor(nowDate.getMonth() / 3) - (prev ? 1 : 0)
      const start = new Date(nowDate.getFullYear(), q * 3, 1).getTime()
      const end = new Date(nowDate.getFullYear(), q * 3 + 3, 1).getTime() - 1
      const sd = new Date(start)
      const qNo = Math.floor(sd.getMonth() / 3) + 1
      return clamp(start, end, `${qNo}-й квартал ${sd.getFullYear()}`)
    }
    case 'year': {
      const y = nowDate.getFullYear() - (prev ? 1 : 0)
      const start = new Date(y, 0, 1).getTime()
      const end = new Date(y + 1, 0, 1).getTime() - 1
      return clamp(start, end, `${y} год`)
    }
    case 'custom':
    default: {
      const from = dateInputMs(customFrom, today)
      const toStart = dateInputMs(customTo, today)
      const to = toStart + DAY_MS - 1
      return {
        from: Math.min(from, to),
        to: Math.max(from, to),
        label: `${fmtFull(Math.min(from, to))} — ${fmtFull(toStart)}`,
        partial: false
      }
    }
  }
}

/** YYYY-MM-DD → epoch-ms начала суток; при пустой строке — запасное значение. */
function dateInputMs(value: string, fallback: number): number {
  if (!value) return fallback
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return fallback
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime()
}
